import type {
  UserCompaniesResponseDto,
  UserResponseDTO,
} from '@relay-towing/api';
import env from '../config/env';

export const SELECTED_COMPANY_KEY = 'relay:selectedCompanyId';

export const getStoredSelectedCompanyId = (): string | null =>
  localStorage.getItem(SELECTED_COMPANY_KEY);

/**
 * `x-company-id` for the selected company, or nothing, for fetches outside
 * driverApi (whose interceptor already adds it). Push registration sends it
 * so the device is registered to the Driver row in that company; a driver
 * in two companies used to be registered to whichever row was found first.
 */
export const selectedCompanyHeader = (): Record<string, string> => {
  const companyId = getStoredSelectedCompanyId();
  return companyId ? { 'x-company-id': companyId } : {};
};

/**
 * Driver profile may return empty companyId for multi-company users; use stored selection (same idea as x-company-id).
 */
export function companyIdFromProfileOrStorage(
  profile: { companyId?: string } | null | undefined
): string {
  if (!profile) return '';
  const trimmed = profile.companyId?.trim();
  if (trimmed) return trimmed;
  if (typeof window === 'undefined') return '';
  return getStoredSelectedCompanyId() ?? '';
}

export const resolveSelectedCompanyId = (
  companies: UserCompaniesResponseDto['companies'],
  storedId: string | null
): string | null =>
  storedId && companies.some((company) => company.company.id === storedId)
    ? storedId
    : (companies[0]?.company.id ?? null);

export const getUserCompanies = async (
  token: string,
  userId: string
): Promise<UserCompaniesResponseDto> => {
  const response = await fetch(`${env.apiUrl}/user-companies/user/${userId}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user companies: ${response.statusText}`);
  }

  return response.json();
};

const getCurrentUser = async (token: string): Promise<UserResponseDTO> => {
  const response = await fetch(`${env.apiUrl}/auth/me`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch the signed-in user: ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Picks the company to send as `x-company-id` and stores it, before the first
 * company request. On a fresh device nothing was stored, so the profile went
 * out with no company, the backend refused it and the splash said "No company
 * found". Both requests here need only a token: the Relay user id is not in
 * it, so /auth/me supplies it.
 *
 * With `refresh`, asks again even when an id is stored, for when the stored
 * one was refused (the user left that company).
 */
export const ensureSelectedCompanyId = async (
  token: string,
  { refresh = false }: { refresh?: boolean } = {}
): Promise<string | null> => {
  const storedId = getStoredSelectedCompanyId();
  if (storedId && !refresh) return storedId;

  const user = await getCurrentUser(token);
  const { companies } = await getUserCompanies(token, user.id);
  const selectedId = resolveSelectedCompanyId(companies, storedId);
  if (selectedId) {
    localStorage.setItem(SELECTED_COMPANY_KEY, selectedId);
  }
  return selectedId;
};
